import React from "react";
import {
  MapPin,
  Building2,
  DollarSign,
  Clock,
  Calendar,
  Users,
  CheckCircle2,
  AlertCircle,
  CircleDot,
} from "lucide-react";
import { motion } from "framer-motion";

/* ── Status configuration (dispoline STATUS_CFG pattern, mapped to officer job statuses) ── */
const JOB_STATUS_CFG = {
  open:      { label: "Open",      bg: "bg-sky-50",     border: "border-sky-200",     text: "text-sky-800",     dot: "bg-sky-500"     },
  disputed:  { label: "Disputed",  bg: "bg-orange-50",  border: "border-orange-200",  text: "text-orange-800",  dot: "bg-orange-500"  },
  completed: { label: "Completed", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
  paid:      { label: "Paid",      bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
  resolved:  { label: "Resolved",  bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
  cancelled: { label: "Cancelled", bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-800",    dot: "bg-rose-500"    },
  expired:   { label: "Expired",   bg: "bg-gray-50",    border: "border-gray-200",    text: "text-gray-700",    dot: "bg-gray-500"    },
};

const StatusBadge = ({ status, size = "md" }) => {
  const cfg = JOB_STATUS_CFG[status] || JOB_STATUS_CFG.open;
  const sizeClass = size === "lg" ? "px-3 py-1 text-sm" : "px-2 py-0.5 text-xs";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${cfg.bg} ${cfg.text} ${cfg.border} ${sizeClass}`}>
      <span className={`rounded-full ${cfg.dot} ${size === "lg" ? "h-2 w-2" : "h-1.5 w-1.5"}`} />
      {cfg.label}
    </span>
  );
};

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const fmtTime = (t) => t || "—";

/* ── Quick-stat card (dispoline AuftraegeTab statistics row style) ── */
const StatCard = ({ icon: Icon, label, value, accent = "text-foreground" }) => (
  <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      {Icon && <Icon size={14} className="text-muted-foreground" />}
    </div>
    <p className={`mt-1 text-2xl font-bold ${accent}`}>{value}</p>
  </div>
);

/* ── Coverage bar (mirrors AuftraegeTab CoverageBar) ── */
const CoverageBar = ({ filled, total }) => {
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const color = pct === 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-card-foreground tabular-nums">
        {filled}/{total} ({pct}%)
      </span>
    </div>
  );
};

const OverviewTab = ({ job, shifts = [], offersMetadata = [], loading }) => {
  if (loading && !job) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }
  if (!job) {
    return (
      <div className="rounded-xl border border-border bg-card p-12 text-center text-sm text-muted-foreground">
        Work order not found.
      </div>
    );
  }

  const stats = job.statistics || {};
  const totalShifts = shifts.length || stats.totalShiftsInJob || 0;
  const totalStaffRequired = stats.totalStaffRequired || shifts.reduce((s, sh) => s + (sh.staffsRequired || 0), 0);
  const shiftsCovered = stats.totalShiftsCovered || 0;
  const acceptedOffers = stats.offerCounts?.accepted || 0;
  const openOffers = offersMetadata.filter((o) => o.status === "pending" || o.status === "open").length;

  return (
    <div className="space-y-6">
      {/* Status + quick stats row */}
      <div className="flex items-center justify-between">
        <StatusBadge status={job.status} size="lg" />
        <div className="text-xs text-muted-foreground">
          Created {fmtDate(job.createdAt)}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon={Calendar} label="Shifts" value={totalShifts} />
        <StatCard icon={Users} label="Staff Required" value={totalStaffRequired} />
        <StatCard icon={CheckCircle2} label="Accepted" value={acceptedOffers} accent="text-emerald-600" />
        <StatCard icon={AlertCircle} label="Open Offers" value={openOffers} accent="text-amber-600" />
      </div>

      {/* Coverage */}
      <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Coverage</h3>
          <CircleDot size={14} className="text-muted-foreground" />
        </div>
        <CoverageBar filled={shiftsCovered} total={totalShifts} />
      </div>

      {/* Details + Customer */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 shadow-sm lg:col-span-2">
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Work Order Details
          </h3>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <div className="flex items-start gap-2">
              <MapPin size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
              <div>
                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Location</dt>
                <dd className="text-sm text-card-foreground">{job.formattedAddress || "—"}</dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <DollarSign size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
              <div>
                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Pay Rate</dt>
                <dd className="text-sm text-card-foreground">${job.hourlyPayRate}/hr</dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
              <div>
                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Timezone</dt>
                <dd className="text-sm text-card-foreground">{job.timezone || "—"}</dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Users size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
              <div>
                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Service Type</dt>
                <dd className="text-sm text-card-foreground">{job.serviceType || job.service || "—"}</dd>
              </div>
            </div>
          </dl>
          {job.memo && (
            <div className="mt-4 rounded-lg bg-muted/30 border border-border p-3">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Notes</p>
              <p className="text-sm text-card-foreground whitespace-pre-wrap">{job.memo}</p>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Customer
          </h3>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Building2 size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-card-foreground">
                {job.customer_company_local_id?.companyFriendlyName ||
                  job.customer_company_local_id?.companyLegalName ||
                  job.customer_company_id?.companyName ||
                  "—"}
              </p>
              {job.customer_company_local_id?.facilityType && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {job.customer_company_local_id.facilityType}
                </p>
              )}
              {job.customer_company_local_id?.contact?.contactPersonName && (
                <p className="mt-2 text-xs text-card-foreground">
                  {job.customer_company_local_id.contact.contactPersonName}
                </p>
              )}
              {job.customer_company_local_id?.contact?.email && (
                <p className="text-xs text-muted-foreground">
                  {job.customer_company_local_id.contact.email}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Shifts list */}
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-border bg-muted/50 px-4 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Shifts ({shifts.length})
          </h3>
        </div>
        {shifts.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No shifts on this work order.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Time</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Staff Required</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {shifts.map((s, i) => (
                <motion.tr
                  key={s._id || i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  className="hover:bg-muted/30"
                >
                  <td className="px-4 py-2 text-sm text-card-foreground">{fmtDate(s.date)}</td>
                  <td className="px-4 py-2 text-sm text-card-foreground tabular-nums">
                    {fmtTime(s.startTime)} – {fmtTime(s.endTime)}
                  </td>
                  <td className="px-4 py-2 text-sm text-card-foreground">{s.staffsRequired || 1}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OverviewTab;
